import _ from "lodash";
import posthog from "posthog-js";
import PathConstants from "../routes/path-constants";
import {
  dynamicSuggestions,
  Screen,
  ScreenPersonalizationInput,
} from "../data/dynamic-suggestions";
import {
  AnswerValue,
  Block,
  BlockType,
  ListLandingBlock,
  SurveyAnswer,
  SurveyContext,
  SurveyNode,
} from "./models";
import { DenyingParty } from "../__generated__/graphql";
import {
  Condition,
  Medication,
  PansPandasMedication,
} from "shared/src/data/medication-condition-pairs";
import { AnswerIds } from "./nodes/answer-ids";

export const getDenyingPartyName = (ctx: SurveyContext): string => {
  if (!ctx.denyingParty) {
    return "";
  }

  switch (ctx.denyingParty) {
    case DenyingParty.InsuranceCompany:
      return ctx.providerName || "";
    case DenyingParty.Pbm:
      return ctx.pbmName || "";
    case DenyingParty.Other:
      return ctx.otherDenyingPartyName || "";
    default:
      return "";
  }
};

export const getAnswer = (
  answers: SurveyAnswer[],
  answerId: string,
): AnswerValue | undefined => answers.find(({ id }) => id === answerId)?.value;

export const isPansOrPandas = (condition: string): boolean =>
  condition === Condition.PANDAS || condition === Condition.PANS;

export const isType2 = (condition: string): boolean =>
  condition === Condition.TYPE_2_DIABETES;

export const isConditionInList = (
  condition: Condition,
  listOfConditions: Condition[],
) => listOfConditions.includes(condition);

export const getFirstAnswer = (
  answers: SurveyAnswer[],
  answerId: string,
): string => {
  const answer = getAnswer(answers, answerId);

  if (_.isNil(answer) || _.isString(answer)) {
    return answer ?? "";
  }
  return _.head(answer) ?? "";
};

// Returns a list of block ids of the children of this node
// note: This includes the blocks of the node passed in.
export function findChildBlocks(
  node: SurveyNode,
  context: SurveyContext,
): Block[] {
  let resp: Block[] = [];

  const blockIds = node.blocks;
  resp = resp.concat(blockIds);

  const children = node.next ? node.next(context) : [];
  children.forEach((child) => {
    resp = resp.concat(findChildBlocks(child, context));
  });

  return resp;
}

export const getBlocksOfType = (
  node: SurveyNode,
  blockType: BlockType,
): Block[] => node.blocks.filter(({ type }) => type === blockType);

// this finds immediate children ids without the node passed in excluded
// NOTE: This counts options as child trees.
export const findImmediateChildIds = (
  node: SurveyNode,
  context: SurveyContext,
): string[] => {
  const optionNodeIds: string[] = getBlocksOfType(
    node,
    BlockType.ListLandingBlock,
  ).flatMap((block) => {
    const llBlock = block as ListLandingBlock;
    return llBlock.options.map(({ getNode }) => getNode(context).id);
  });

  return optionNodeIds.concat(
    node.next ? node.next(context).map(({ id }) => id) : [],
  );
};

// Given a node, remove all of the answers related to it and its children and return an updated context
export function removeChildAnswers(
  node: SurveyNode,
  context: SurveyContext,
): SurveyContext {
  const childBlocks: string[] = _.compact(
    findChildBlocks(node, context).map(({ answerId }) => answerId),
  );
  const filteredAnswers = context.answers.filter(
    ({ id }) => !childBlocks.includes(id),
  );

  return { ...context, answers: filteredAnswers };
}

export const getSuggestions = (
  conditionToFind: string,
  screenName: string | undefined,
): string[] => {
  const searchObject = { Condition: conditionToFind };

  const spi = _.find<ScreenPersonalizationInput>(
    dynamicSuggestions.ScreenPersonalizationInputs,
    searchObject,
  );
  if (spi) {
    const screen: Screen | undefined = _.find(spi.Screens, {
      Screen_Name: screenName ?? "Symptom_Story",
    });
    if (screen) {
      return _.filter(screen.Fields, { Field_Name: "Suggestions" })?.map(
        (s) => s.Field_Value,
      );
    }
  }
  return [];
};

export const getSurveyAnswerStrings = (
  surveyAnswers: SurveyAnswer[],
  answerId: string,
): string[] => [...(<string[]>getAnswer(surveyAnswers, answerId) ?? [])];

export const pauseAppeal = (
  close: () => void,
  navigate: (path: string) => void,
  ctx: SurveyContext,
  node_id: string,
) => {
  posthog.capture("patient_appeal_paused", {
    appealId: ctx?.appealId,
    exitSource: node_id,
    condition: ctx.condition,
    medication: ctx.medication,
    denial_reason: ctx.denialReason,
    sub_denial_reason: ctx.subDenialReasons,
    denying_party_name: getDenyingPartyName(ctx),
  });

  navigate(PathConstants.Appeals);
};

export const continueAppealEvent = (ctx: SurveyContext, node_id: string) => {
  posthog.capture("patient_appeal_continued_kickout", {
    appealId: ctx?.appealId,
    continueSource: node_id,
    condition: ctx.condition,
    medication: ctx.medication,
    denial_reason: ctx.denialReason,
    sub_denial_reason: ctx.subDenialReasons,
    denying_party_name: getDenyingPartyName(ctx),
  });
};

export function getBmiFromAnswers(context: SurveyContext): number | undefined {
  const heightInInches = Number(
    getAnswer(context.answers, AnswerIds.HEALTH_IMPACT_PATIENT_HEIGHT),
  );
  const currentWeight = Number(
    getAnswer(context.answers, AnswerIds.HEALTH_IMPACT_PATIENT_CURRENT_WEIGHT),
  );

  const BMI_CONVERSION_FACTOR = 703;
  const calculatedBmi =
    (currentWeight / heightInInches ** 2) * BMI_CONVERSION_FACTOR;

  return calculatedBmi;
}

const medicationToMedicationWithGenericMap: {
  [key in Medication | PansPandasMedication]?: string;
} = {
  [Medication.WEGOVY]: `${Medication.WEGOVY} (semaglutide)`,
  [Medication.OZEMPIC]: `${Medication.OZEMPIC} (semaglutide)`,
  [Medication.RYBELSUS]: `${Medication.RYBELSUS} (semaglutide)`,
  [Medication.ZEPBOUND]: `${Medication.ZEPBOUND} (tirzepatide)`,
  [Medication.MOUNJARO]: `${Medication.MOUNJARO} (tirzepatide)`,
};

export const getBrandedMedicationWithGenericConditionally = (
  medication: string,
): string => medicationToMedicationWithGenericMap[medication] ?? medication;
